import React, { useCallback } from 'react'

import {
  formatIdleCountdown,
  useKeycloakSessionGuard,
  type SignedOutReason,
} from '@sweetauth/keycloak'
import { toast } from 'sonner'

import { providerName, useLogoutFunction } from '@sweetauth/core'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/common/AlertDialog'

/** Re-exported for `_app`, which owns the signed-out reason and passes it back in. */
export type { SignedOutReason }

const SIGNED_OUT_COPY: Record<SignedOutReason, string> = {
  inactivity: 'You were signed out because you were inactive for a while.',
  session_limit:
    'For security, sessions are limited to 12 hours. Yours reached that limit, so you were signed out — please sign in again to continue.',
  expired: 'Your session has expired. Please sign in again to continue.',
}

interface SessionExpiryGuardProps {
  /** The reason the session ended, or null while it's active. Owned by `_app` — the common
   *  parent of the auth store (which reports a mid-session death via `onUnauthenticated`) and
   *  this guard (whose idle/cap watchdog is the other source) — so both feed one value. */
  signedOutReason: SignedOutReason | null
  /** Report that the session ended. `_app` keeps the first reason. */
  onSessionEnded: (reason: SignedOutReason) => void
}

/**
 * Session-lifecycle guard for the Keycloak (FedRAMP) build. The timing/activity logic — token
 * refresh on genuine activity and on tab-return, the ~15-min idle warn/sign-out, and the 12-hour
 * absolute cap — lives in @sweetauth/keycloak's useKeycloakSessionGuard (shared with the admin
 * app). This component owns only the www_v2 UI:
 *  - an extendable "Stay signed in" prompt before the idle lock;
 *  - a cap-warning toast before the hard 12-hour sign-out (AC-12);
 *  - a terminal signed-out dialog (idle, cap, or a store-reported mid-session death) whose
 *    "Sign in" starts login FRESH — so the user never lands on a stale Keycloak login page.
 *
 * Renders nothing for PropelAuth, whose SDK owns its own session lifetime.
 */
export const SessionExpiryGuard: React.FC<SessionExpiryGuardProps> = (props) =>
  providerName === 'keycloak' ? <KeycloakSessionExpiryGuard {...props} /> : null

const KeycloakSessionExpiryGuard: React.FC<SessionExpiryGuardProps> = ({
  signedOutReason,
  onSessionEnded,
}) => {
  const logout = useLogoutFunction()
  // Cap heads-up (AC-12): a toast ~5 min before the hard 12h sign-out.
  const onCapWarning = useCallback((minutes: number) => {
    toast.warning(
      `Your 12-hour session limit is almost up — you'll be signed out in about ${minutes} minute${minutes === 1 ? '' : 's'}. Please save your work.`,
      { duration: 20000 },
    )
  }, [])

  const { idleWarnOpen, idleRemaining, handleStay } = useKeycloakSessionGuard({
    signedOutReason,
    onSessionEnded,
    onCapWarning,
  })

  // "Sign in" / "Log out": end the Keycloak session (still valid server-side when the idle
  // warning's "Log out" is used), then start login fresh. Awaiting the logout ensures Keycloak
  // prompts for credentials rather than silently SSO-ing the user back in.
  const handleSignIn = useCallback(() => {
    void logout(true)
  }, [logout])

  // Terminal signed-out dialog — takes precedence and offers only a fresh sign-in. The overlay
  // is opaque (not the default dim) so the locked page is CONCEALED, not merely dimmed —
  // FedRAMP AC-11(01) pattern-hiding display.
  if (signedOutReason) {
    return (
      <AlertDialog open>
        <AlertDialogContent overlayClassName="bg-white dark:bg-primary-900">
          <AlertDialogHeader>
            <div className="mb-1 flex">
              <img
                src="/sweetspot-wordmark-v2.svg"
                alt=""
                aria-hidden
                width={3047}
                height={512}
                className="h-6 w-auto dark:hidden"
                draggable={false}
              />
              <img
                src="/logo-white.png"
                alt=""
                aria-hidden
                width={1190}
                height={200}
                className="hidden h-6 w-auto dark:block"
                draggable={false}
              />
            </div>
            <AlertDialogTitle>You&apos;ve been signed out</AlertDialogTitle>
            <AlertDialogDescription>
              {SIGNED_OUT_COPY[signedOutReason]}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:flex-col">
            <AlertDialogAction
              variant="primary-compact"
              className="w-full"
              onClick={handleSignIn}
            >
              Sign in
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )
  }

  return (
    <AlertDialog open={idleWarnOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Still there?</AlertDialogTitle>
          <AlertDialogDescription>
            For your security, you&apos;ll be signed out due to inactivity in{' '}
            {formatIdleCountdown(idleRemaining)}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel variant="outline-compact" onClick={handleSignIn}>
            Log out
          </AlertDialogCancel>
          <AlertDialogAction variant="primary-compact" onClick={handleStay}>
            Stay signed in
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
